import React, { useMemo, useEffect, useRef } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled, { css } from 'styled-components';
import { TEXT_STYLES, TextVariant } from '@components/shared/design/Typography';
import { INSTAGRAM_LINK } from '@constants';
import { PageSlice, SliceImageGridItem } from '@contracts';
import Carousel from './shared/Carousel';
import Colors from './shared/Colors';
import FadeIn from './shared/FadeIn';
import LinkButton from './shared/LinkButton';

const carouselOptions = { initialIndex: 0, draggable: true, prevNextButtons: true };

interface Props {
  slice?: PageSlice;
}

const ImageGridCarousel: React.FC<Props> = ({ slice }) => {
  const { t } = useTranslation();
  const sliceItems = slice?.items as SliceImageGridItem[];
  const carouselItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const imageWrapperRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleImageLoad = () => {
    let maxHeight = 0;

    carouselItemRefs.current.forEach((carouselItem) => {
      maxHeight = Math.max(maxHeight, carouselItem?.clientHeight || 0);
    });

    carouselItemRefs.current.forEach((carouselItem, index) => {
      if (carouselItem && imageWrapperRefs.current[index] && maxHeight > 100) {
        imageWrapperRefs.current[index]!.style.height = `${maxHeight}px`;
      }
    });
  };

  return (
    <FadeIn>
      <Container>
        <Header>
          <HeaderTexts>
            <Overlay>@yourdaye</Overlay>
            <Heading>{t('components.our-community')}</Heading>
            <Description>{t('components.join-over-people', { count: 80 })}</Description>
          </HeaderTexts>
          <LinkButton to={INSTAGRAM_LINK} color={Colors.navyBlue}>
            {t('components.follow-us')}
          </LinkButton>
        </Header>
        <CarouselWrapper $sliceCount={sliceItems?.length}>
          {useMemo(
            () => (
              <Carousel options={carouselOptions}>
                {sliceItems?.map((item, index) =>
                  item?.image?.gatsbyImageData ? (
                    <CarouselItem key={index} ref={(el) => (carouselItemRefs.current[index] = el)}>
                      <ImageWrapper ref={(el) => (imageWrapperRefs.current[index] = el)}>
                        <GatsbyImage image={item.image.gatsbyImageData} alt={item.image.alt || ''} onLoad={handleImageLoad} />
                      </ImageWrapper>
                    </CarouselItem>
                  ) : null
                )}
              </Carousel>
            ),
            [sliceItems]
          )}
        </CarouselWrapper>
        <MobileLinkButton to={INSTAGRAM_LINK} color={Colors.navyBlue}>
          {t('components.follow-us')}
        </MobileLinkButton>
      </Container>
    </FadeIn>
  );
};

export default ImageGridCarousel;

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 96px 144px;
  overflow: hidden;
  max-width: 1440px;
  margin: 0 auto;

  a {
    border-radius: 2px;
  }

  @media (max-width: 1400px) {
    padding: 56px 25px;
  }

  @media (max-width: 600px) {
    padding: 56px 16px;
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 1024px) {
    a {
      display: none;
    }
  }
`;

const ImageWrapper = styled.div`
  height: 100%;

  > div {
    height: 100%;
  }
`;

const HeaderTexts = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  align-items: flex-start;
`;

const Overlay = styled.div`
  color: ${Colors.navyBlue};
  ${TEXT_STYLES[TextVariant.UpperText4]};

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.UpperText5]};
  }
`;

const Description = styled.div`
  margin-top: 8px;
  color: ${Colors.navyBlue};
  ${TEXT_STYLES[TextVariant.BodyRegularLG]};
`;

const Heading = styled.div`
  color: ${Colors.navyBlue};
  ${TEXT_STYLES[TextVariant.Heading3]};

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.Heading5]};
  }
`;

const CarouselWrapper = styled.div<{ $sliceCount: number }>`
  margin-top: 32px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  min-width: 100%;
  position: relative;

  > div {
    width: 100%;
  }

  .carousel {
    width: 100%;
  }

  .flickity-viewport {
    width: 100%;
    overflow: hidden;

    @media (max-width: 1456px) {
      overflow: visible;
    }
  }

  .carousel:after {
    content: 'flickity';
    display: none;
  }

  .flickity-prev-next-button {
    border-radius: 50% !important;
    width: 32px !important;
    height: 32px !important;
    background: ${Colors.paleGold50} !important;
    border: 1px solid ${Colors.paleGold} !important;

    ${(props) =>
      props.$sliceCount < 5
        ? css`
            display: none !important;
          `
        : ''}

    &.next {
      right: -56px !important;
    }

    &.previous {
      left: -56px !important;
    }
  }

  @media (max-width: 1456px) {
    .flickity-prev-next-button {
      display: none;
    }
  }
`;

const CarouselItem = styled.div`
  width: 270px;
  border-radius: 12px;
  margin-right: 24px;
  overflow: hidden;
  min-height: 100%;
`;

const MobileLinkButton = styled(LinkButton)`
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    margin-top: 32px;
  }
`;
